<template>
  <fieldset class="tw:min-w-0">
    <div class="bg-light rounded">
      <template v-if="canSetup">
        <div
          v-if="dataLocales.length > 1"
          class="border-bottom border-secondary px-3 px-md-4 py-2 py-md-3"
        >
          <div role="tablist" :aria-label="$t('Select language')" class="tw:flex tw:flex-wrap tw:gap-2">
            <LocaleStatusBadge
              v-for="locale in dataLocales"
              :key="locale.value"
              as="button"
              type="button"
              role="tab"
              :aria-selected="currentLang === locale.value"
              :locale="locale"
              :completed="completedLocales.has(locale.value)"
              :active="currentLang === locale.value"
              @click="currentLang = locale.value"
            />
          </div>
        </div>

        <div ref="slotContainer" class="px-3 px-md-4 py-2 py-md-3">
          <slot :lang="currentLang" />
        </div>
      </template>

      <Alert v-else variant="danger">
        {{ $t('You must set up languages before setup custom fields') }}
      </Alert>
    </div>
  </fieldset>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Alert from '@/components/ui/Alert.vue'
import LocaleStatusBadge from '@/components/ui/LocaleStatusBadge.vue'

const props = defineProps({
  dataLocales: {
    type: Array,
    default: () => []
  },
  dataDefaultLocale: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['lang-change'])

const currentLang = ref(props.dataDefaultLocale)
const slotContainer = ref(null)
const completedLocales = ref(new Set())

const canSetup = computed(() => props.dataLocales.length > 0)

const isFieldFilled = (el) => {
  if (!el) {
    return false
  }

  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
    return (el.value ?? '').toString().trim().length > 0
  }

  const editable = el.querySelector('[contenteditable="true"]')
  if (editable) {
    return editable.textContent.trim().length > 0
  }

  const native = el.querySelector('input, textarea, select')
  if (native) {
    return (native.value ?? '').toString().trim().length > 0
  }

  const attr = el.getAttribute('value')
  if (attr !== null) {
    return attr.trim().length > 0
  }

  return false
}

const recomputeCompletion = () => {
  if (!slotContainer.value) {
    return
  }

  const next = new Set()
  slotContainer.value.querySelectorAll('[data-locale]').forEach((el) => {
    const locale = el.getAttribute('data-locale')
    if (locale && isFieldFilled(el)) {
      next.add(locale)
    }
  })

  completedLocales.value = next
}

const onFieldChange = () => recomputeCompletion()

onMounted(async () => {
  if (props.dataLocales.length > 0) {
    currentLang.value = props.dataLocales.find((l) => l.value === props.dataDefaultLocale)?.value
      ?? props.dataLocales[0].value
  }

  await nextTick()
  recomputeCompletion()

  slotContainer.value?.addEventListener('input', onFieldChange)
  slotContainer.value?.addEventListener('change', onFieldChange)
})

onBeforeUnmount(() => {
  slotContainer.value?.removeEventListener('input', onFieldChange)
  slotContainer.value?.removeEventListener('change', onFieldChange)
})

watch(currentLang, (value) => emit('lang-change', value))
</script>
